import { CreateEmailTemplateDto } from "@dune/types";
import { Button, ResizableLayout, Skeleton } from "@dune/ui";
import { ArrowLeft, Save } from "lucide-react";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
	AvailableFieldsPanel,
	EmailTemplateForm,
	EmailTemplateFormData,
	useEmailTemplateEditor,
} from "./components";

const EmailTemplateEditorPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const copyFromId = searchParams.get("copyFrom");
	const initialName = searchParams.get("name");

	const {
		control,
		errors,
		handleSubmit,

		isLoading,
		error,

		template,
		copyTemplate,
		isEditing,
		isCopying,

		createTemplate,
		updateTemplate,

		availableFields,
		handleCopyPlaceholder,
		invalidPlaceholders,

		uploadImage,
	} = useEmailTemplateEditor({
		id,
		copyFromId: copyFromId || undefined,
		initialName: initialName || undefined,
	});

	const onSubmit = async (data: EmailTemplateFormData) => {
		try {
			if (isEditing && id) {
				await updateTemplate.mutateAsync({
					id,
					updateDto: {
						name: data.name,
						subject: data.subject,
						htmlContent: data.htmlContent,
						labels: data.labels,
					},
				});
				toast.success("Template mis à jour avec succès");
			} else {
				const createDto: CreateEmailTemplateDto = {
					name: data.name,
					subject: data.subject,
					htmlContent: data.htmlContent,
					entityModel: data.entityModel,
					labels: data.labels,
				};
				const result = await createTemplate.mutateAsync(createDto);

				if (result.invalidFields && result.invalidFields.length > 0) {
					toast.warning("Template créé avec des champs invalides", {
						description: `Champs invalides détectés: ${result.invalidFields.join(", ")}`,
						duration: 8000,
					});
				} else {
					toast.success("Template créé avec succès");
				}
			}
			navigate("/document-templates?tab=email-templates");
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { invalidFields?: string[] } };
				};
				if (axiosError.response?.data?.invalidFields) {
					const invalidFields = axiosError.response.data.invalidFields;
					toast.error("Erreur de validation", {
						description: `Champs invalides: ${invalidFields.join(", ")}`,
						duration: 10000,
					});
					return;
				}
			}
			toast.error(
				isEditing
					? "Erreur lors de la mise à jour"
					: "Erreur lors de la création",
			);
		}
	};

	if (isLoading) {
		return (
			<div className="p-6">
				<Skeleton className="h-8 w-64 mb-4" />
				<div className="grid grid-cols-2 gap-6">
					<div className="space-y-4">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-64 w-full" />
					</div>
					<div className="space-y-4">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-64 w-full" />
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-6">
				<div className="text-center py-12">
					<h3 className="text-lg font-medium text-destructive mb-2">
						Erreur de chargement
					</h3>
					<p className="text-sm text-muted-foreground">
						{error?.message || "Une erreur est survenue"}
					</p>
					<Button
						onClick={() => navigate("/document-templates")}
						className="mt-4"
					>
						Retour
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="h-[calc(100vh-4rem)]">
			<ResizableLayout className="h-full">
				<ResizableLayout.Header className="border-b px-6 py-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-4">
							<Button
								variant="ghost"
								onClick={() =>
									navigate("/document-templates?tab=email-templates")
								}
								className="flex items-center gap-2"
							>
								<ArrowLeft className="w-4 h-4" />
								Retour
							</Button>
							<div>
								<h1 className="text-xl font-semibold">
									{isEditing
										? `Modifier le template "${template?.name}"`
										: isCopying
											? `Copier le template "${copyTemplate?.name}"`
											: "Nouveau template d'email"}
								</h1>
								<p className="text-sm text-muted-foreground">
									Créez un template d'email avec des placeholders dynamiques
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<Button
								onClick={handleSubmit(onSubmit)}
								disabled={createTemplate.isPending || updateTemplate.isPending}
								className="flex items-center gap-2"
							>
								<Save className="w-4 h-4" />
								{createTemplate.isPending || updateTemplate.isPending
									? "Sauvegarde..."
									: "Sauvegarder"}
							</Button>
						</div>
					</div>
				</ResizableLayout.Header>

				<ResizableLayout.Main>
					<form onSubmit={handleSubmit(onSubmit)}>
						<EmailTemplateForm
							control={control}
							errors={errors}
							isEditing={isEditing}
							onImageUpload={uploadImage}
							invalidPlaceholders={invalidPlaceholders}
							template={template}
							copyTemplate={copyTemplate}
							isCopying={isCopying}
						/>
					</form>
				</ResizableLayout.Main>

				<ResizableLayout.Panel>
					<ResizableLayout.PanelHeader>
						<ResizableLayout.PanelTitle>
							Champs disponibles
						</ResizableLayout.PanelTitle>
					</ResizableLayout.PanelHeader>
					<ResizableLayout.PanelContent>
						<AvailableFieldsPanel
							fields={availableFields}
							onCopyPlaceholder={handleCopyPlaceholder}
						/>
					</ResizableLayout.PanelContent>
				</ResizableLayout.Panel>
			</ResizableLayout>
		</div>
	);
};

export default EmailTemplateEditorPage;
